import React, { ChangeEvent, FormEvent } from "react";
import { Prompt } from "react-router-dom";
import { RouteComponentPropsHistory } from "../../types";

import ViewToolbar from "../ViewToolbar";
import ValidateInput from "../ValidateInput";
import DeletionConfirmationDialog from "../DeletionConfirmationDialog";
import ToolbarMenuItem from "../ToolbarMenuItem";
import OverflowMenuItem from "../OverflowMenuItem";

import withAuth from "../withAuth";
import withTitle from "../withTitle";

import { V1, V2 } from "../../Api";
import { WellKnownGroups, IAuthProps } from "../AuthContext";
import { formatName, formatPhone, formatTelephonePartial, validatePhoneNumberOptional } from "../../Utilities";
import NoteBox from "../NoteBox";
import { DateTime } from "luxon";

interface IMarketingLeadsCreationViewState {
    isLoading: boolean | string;
    isSubmitting: boolean | string;
    isDeleteDialogOpen: boolean;
    hasChanged: boolean;
    canEdit: boolean;
    isEdit: boolean;

    leadId: string;
    companyId: string;
    name: string;
    contactName: string;
    contactPhone: string;
    contactEmail: string;
    assignedEmployee: string;
    notes: any[];

    marketers: any[];
}

class MarketingLeadsCreationView extends React.Component<RouteComponentPropsHistory<any, any> & IAuthProps, IMarketingLeadsCreationViewState> {
    private inputs: ValidateInput[];
    constructor(props: Readonly<RouteComponentPropsHistory<any, any> & IAuthProps>) {
        super(props);

        const state: IMarketingLeadsCreationViewState = {
            isLoading: true,
            isSubmitting: false,
            isDeleteDialogOpen: false,
            hasChanged: false,
            canEdit: props.authContext.hasRight("lead:write"),
            isEdit: !!props.match.params.id,

            leadId: "",
            companyId: "",
            name: "",
            contactName: "",
            contactPhone: "",
            contactEmail: "",
            assignedEmployee: "",
            notes: [],

            marketers: []
        };

        this.state = state;

        this.receiveValidationRef = this.receiveValidationRef.bind(this);
        this.formInputChanged = this.formInputChanged.bind(this);
        this.toggleDeleteDialog = this.toggleDeleteDialog.bind(this);
        this.realDeleteLead = this.realDeleteLead.bind(this);
        this.submitForm = this.submitForm.bind(this);
        this.cancelForm = this.cancelForm.bind(this);
        this.handleNewNote = this.handleNewNote.bind(this);
        this.handleDeleteNote = this.handleDeleteNote.bind(this);
        this.handleDeleteSuccess = this.handleDeleteSuccess.bind(this);

        this.inputs = [];
    }

    public async componentDidMount() {
        try {
            const marketers = await V1.get<any[]>(`employees/summary?group=${WellKnownGroups.Marketer}`);
            let lead: any = {};
            if (this.props.match.params.id) {
                lead = await V2.get<any>(`leads/${this.props.match.params.id}`);
            }
            else if (this.props.location.state && this.props.location.state.company) {
                const company = this.props.location.state.company;

                lead = {
                    companyId: company.id,
                    name: company.name,
                    contactName: company.contact.name,
                    contactPhone: company.contact.phone,
                    contactEmail: company.contact.email
                };
            }


            this.setState({
                isLoading: false,

                marketers: marketers.sort((a, b) => formatName(a.name).localeCompare(formatName(b.name))),

                companyId: lead.companyId,
                leadId: lead.id || "",
                name: lead.name || "",
                contactName: lead.contactName || "",
                contactEmail: lead.contactEmail || "",
                contactPhone: lead.contactPhone ? formatPhone(lead.contactPhone) : "",
                assignedEmployee: lead.assignedEmployee ? lead.assignedEmployee.id : "",
                notes: lead.notes ? lead.notes.map((a: any) => {
                    a.author = formatName(a.employee.name);
                    return a;
                }) : []
            });
        }
        catch (e) {
            this.setState({
                isLoading: e.message
            });
        }
    }

    public render() {
        if (this.state.isLoading === true) {
            return <div className="viewer-root">
                <div className="spinner spinner-center lds-dual-ring-big" />
            </div>;
        }
        if (typeof this.state.isLoading === "string") {
            return <div className="viewer-root">
                <div className="error-box">{this.state.isLoading}</div>
            </div>;
        }

        return (
            <div className="viewer-root with-actionbar">
                <Prompt when={this.state.canEdit && this.state.hasChanged} message="Your changes will not be saved if you continue." />
                <ViewToolbar>
                    <OverflowMenuItem>
                        <ToolbarMenuItem right="lead:delete" condition={!!this.props.match.params.id}>
                            <button type="button" className="red" onClick={this.toggleDeleteDialog}><i className="icon-trash" /><span>Delete Lead</span></button>
                        </ToolbarMenuItem>
                    </OverflowMenuItem>
                </ViewToolbar>
                <DeletionConfirmationDialog isOpen={this.state.isDeleteDialogOpen} onRequestClose={this.toggleDeleteDialog} onConfirm={this.realDeleteLead} onSuccess={this.handleDeleteSuccess} />
                <form className={"input-form" + (!this.state.canEdit ? " form-readonly" : "")} onSubmit={this.submitForm}>
                    <div className="column-root">
                        <section className="info-block">
                            <header>
                                <h3>Lead Info</h3>
                            </header>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-name" className="required">Name of Lead</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-name" name="name" value={this.state.name} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={true} readOnly={!this.state.canEdit} />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-contact-name">Name of Contact Person</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-contact-name" name="contactName" value={this.state.contactName} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={false} readOnly={!this.state.canEdit} />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-contact-email">Contact Email</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-contact-email" name="contactEmail" value={this.state.contactEmail} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={false} readOnly={!this.state.canEdit} />
                                </div>
                                <div className="input-form-group">
                                    <label htmlFor="frm-contact-phone">Contact Phone</label>
                                    <ValidateInput type="text" ref={this.receiveValidationRef} id="frm-contact-phone" name="contactPhone" value={this.state.contactPhone} onChange={this.formInputChanged} autoComplete="off_please_chrome" isRequired={false} readOnly={!this.state.canEdit} filter={formatTelephonePartial} validator={validatePhoneNumberOptional} />
                                </div>
                            </section>
                            <section>
                                <div className="input-form-group">
                                    <label htmlFor="frm-assigned">Assigned Marketer</label>
                                    <select id="frm-assigned" name="assignedEmployee" value={this.state.assignedEmployee} onChange={this.formInputChanged} autoComplete="off_please_chrome" disabled={!this.state.canEdit}>
                                        <option value="">Unassigned</option>
                                        {this.state.marketers.map(a => <option key={a.id} value={a.id}>{formatName(a.name)}</option>)}
                                    </select>
                                </div>
                            </section>
                        </section>
                        <section className="info-block">
                            <header>
                                <h3>Notes</h3>
                            </header>
                            <NoteBox notes={this.state.notes} displayName={this.props.authContext.user.displayName} onNewNote={this.handleNewNote} onNoteDelete={this.handleDeleteNote} allowDeletion={this.props.authContext.hasRight("lead:delete")} hideControls={!this.state.isEdit && this.state.notes.length > 0} />
                        </section>
                    </div>
                    &nbsp;
                    {
                        this.state.isSubmitting === true
                            ? <div className="spinner lds-dual-ring" />
                            : typeof this.state.isSubmitting === "string"
                                ? <span className="error-text">{this.state.isSubmitting}</span>
                                : null
                    }
                    {this.state.canEdit && this.state.hasChanged
                        ? <div className="action-panel">
                            <button type="submit" className="button text-with-icon push-right"><i className="icon-check" /><span>Save</span></button>
                            <button type="button" className="button text-with-icon" onClick={this.cancelForm}><i className="icon-times" /><span>Cancel</span></button>
                        </div>
                        : <div className="action-panel">
                            <button type="button" className="button push-right" onClick={this.cancelForm}>Ok</button>
                        </div>}
                </form>
            </div>
        );
    }

    private receiveValidationRef(ref: ValidateInput) {
        this.inputs.push(ref);
    }

    private formInputChanged(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) {
        const value = e.currentTarget.value;
        const name = e.currentTarget.name;

        this.setState({
            hasChanged: true,

            [name]: value
        } as any);
    }

    private async submitForm(e: FormEvent) {
        e.preventDefault();

        try {
            this.setState({
                isSubmitting: true,
            });

            const data = {
                companyId: this.state.companyId,
                name: this.state.name,
                contactName: this.state.contactName,
                contactPhone: this.state.contactPhone.replace(/[^0-9]/g, ""),
                contactEmail: this.state.contactEmail,
                assignedEmployeeId: this.state.assignedEmployee || null,
                note: !this.state.isEdit && this.state.notes.length > 0 ? this.state.notes[0].body : undefined
            };

            if (this.state.isEdit) {
                await V2.patch<any[]>(`leads/${this.state.leadId}`, data);
            }
            else {
                await V2.post<any[]>(`leads`, data);
            }

            this.setState({
                hasChanged: false,
                isSubmitting: false,
            });

            this.props.history.goBack();
        }
        catch (e) {
            this.setState({
                isSubmitting: e.message || e || "Could not load data"
            });
        }
    }

    private cancelForm() {
        this.props.history.goBack();
    }

    private toggleDeleteDialog() {
        this.setState(s => ({
            isDeleteDialogOpen: !s.isDeleteDialogOpen
        }));
    }

    private async realDeleteLead() {
        await V2.delete(`leads/${this.state.leadId}`);
    }

    private handleDeleteSuccess() {
        this.props.history.goBack();
    }

    private async handleNewNote(body: string) {
        const note = {
            body: body
        };

        let newNote;
        if (this.state.isEdit) {
            newNote = await V2.post<any>(`leads/${this.state.leadId}/notes`, note);
            newNote.author = formatName(newNote.employee.name);
        }
        else {
            newNote = {
                author: this.props.authContext.user.displayName,
                body: body,
                timestamp: DateTime.local().toISO()
            };
        }

        const notes = this.state.notes;
        notes.push(newNote);

        this.setState({ notes });
    }

    private async handleDeleteNote(index: number) {
        const notes = this.state.notes;
        const noteId = notes[index].noteId;
        if (!noteId) {
            return;
        }

        await V2.delete(`leads/${this.state.leadId}/notes/${noteId}`);

        notes.splice(index, 1);

        this.setState({
            notes
        });
    }
}

export default withAuth(withTitle("Marketing Leads", MarketingLeadsCreationView));